import React, { useState, useEffect } from 'react';
import axios from "axios";
import { Box, Typography, Button, useTheme, Paper, CircularProgress, Snackbar, Alert } from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import ProcessImprovementForm from './ProcessImprovementForm';
import ProcessImprovementList from './ProcessImprovementList';
import { base_url } from '../../config/config';
import { getToken } from "../../config/config";

const ProcessImprovementPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // State
  const [processes, setProcesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Fetch process improvements on component mount
  useEffect(() => {
    fetchProcessImprovements();
  }, []);

  // Fetch process improvements
  const fetchProcessImprovements = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${base_url}/v1/getAllProcessImprovements`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      // Ensure mobile signatures are properly handled
      const processesWithSignatures = (response.data.data || []).map(process => ({
        ...process,
        mobileSignatures: process.mobileSignatures || []
      }));
      
      setProcesses(processesWithSignatures);
    } catch (error) {
      console.error('Error fetching process improvements:', error);
      setError(error.response?.data?.message || 'Failed to fetch process improvements');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle new process improvement creation
  const handleProcessCreated = (newProcess) => {
    setProcesses([newProcess, ...processes]);
    setShowForm(false);
    setNotification({
      open: true,
      message: 'Prozessverbesserung erfolgreich erstellt!',
      severity: 'success'
    });
  };

  // Toggle form visibility
  const toggleForm = () => {
    setShowForm(!showForm);
  };

  // Handle close notification
  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  return (
    <Box m="20px">
      <Header 
        title="PROZESSVERBESSERUNGEN" 
        subtitle="Verwalten und verfolgen Sie Prozessverbesserungen in Ihrer Organisation" 
      />

      <Box 
        display="flex" 
        justifyContent="flex-end" 
        mb="20px"
      >
        <Button
          onClick={toggleForm}
          variant="contained"
          sx={{
            backgroundColor: showForm ? colors.redAccent[500] : colors.blueAccent[700],
            '&:hover': {
              backgroundColor: showForm ? colors.redAccent[700] : colors.blueAccent[800],
            },
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
          }}
        >
          {showForm ? 'ABBRECHEN' : 'NEUE PROZESSVERBESSERUNG ERSTELLEN'}
        </Button>
      </Box>

      {showForm && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 4,
            backgroundColor: colors.primary[400],
            borderRadius: "10px",
          }}
        >
          <ProcessImprovementForm 
            onProcessCreated={handleProcessCreated} 
            colors={colors}
          />
        </Paper>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={5}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box 
          p={3} 
          bgcolor={colors.redAccent[500]} 
          color={colors.grey[100]}
          borderRadius="10px"
          mb={4}
        >
          <Typography variant="h5" fontWeight="bold" mb={1}>
            Fehler beim Laden der Prozessverbesserungen
          </Typography>
          <Typography variant="body1">{error}</Typography>
        </Box>
      ) : (
        <ProcessImprovementList 
          processes={processes} 
          colors={colors}
          onRefresh={fetchProcessImprovements}
        />
      )}
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcessImprovementPage;